// ═══════════════════════════════════════════════════════════════
// HotWash — Analytics
// Charts and breakdowns
// ═══════════════════════════════════════════════════════════════

import { useEffect } from 'react';
import {
  Text, Paper, SimpleGrid, Group, Stack, Progress, Badge,
} from '../../components/gab/GabComponents';
import { useHotWashStore } from '../../store/hotwashStore';
import { useAuthStore } from '../../store/authStore';
import { TIER_META, TIER_KEYS, CATEGORIES, PHASES } from '../../types';
import { primary, semantic } from '../../lib/colors';

export default function Analytics() {
  const { observations, loading: isLoading, loadObservations } = useHotWashStore();
  const { incidents, activeIncident } = useAuthStore();

  useEffect(() => { loadObservations(); }, [loadObservations]);

  const filtered = activeIncident
    ? observations.filter(o => String(o.incidentId) === String(activeIncident.id))
    : observations;

  const total = filtered.length;

  if (total === 0 && !isLoading) {
    return (
      <Paper p="xl" radius="sm" withBorder>
        <Text ta="center" c="dimmed">No observations to analyze. Submit some observations first.</Text>
      </Paper>
    );
  }

  // Tier distribution
  const tierData = TIER_KEYS.map(t => ({
    tier: t,
    count: filtered.filter(o => o.tier === t).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.tier === t).length / total) * 100) : 0,
    ...(TIER_META[t] ?? TIER_META['observation']),
  }));

  // Category breakdown
  const catData = CATEGORIES.map(c => ({
    category: c,
    count: filtered.filter(o => o.category === c).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.category === c).length / total) * 100) : 0,
  })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);

  // Phase breakdown
  const phaseData = PHASES.map(p => ({
    phase: p,
    count: filtered.filter(o => o.phase === p).length,
    pct: total > 0 ? Math.round((filtered.filter(o => o.phase === p).length / total) * 100) : 0,
  })).filter(p => p.count > 0).sort((a, b) => b.count - a.count);

  // Remedy coverage
  const withRemedy = filtered.filter(o => o.suggestedRemedy).length;
  const remedyPct = total > 0 ? Math.round((withRemedy / total) * 100) : 0;

  // Contributors
  const contributors = [...new Set(filtered.map(o => o.submittedBy))];

  // Per-incident breakdown
  const incidentBreakdown = incidents
    .filter(i => i.id != null)
    .map(i => ({
      incident: i,
      count: observations.filter(o => String(o.incidentId) === String(i.id)).length,
      critical: observations.filter(o => String(o.incidentId) === String(i.id) && o.tier === 'critical_error').length,
      remedied: observations.filter(o => String(o.incidentId) === String(i.id) && o.suggestedRemedy).length,
    }))
    .filter(i => i.count > 0)
    .sort((a, b) => b.count - a.count);

  // Missing remedies on critical/problem items
  const unremediedCritical = filtered.filter(o => !o.suggestedRemedy && (o.tier === 'critical_error' || o.tier === 'problem_identified'));

  return (
    <div>
      {/* Key Metrics Row */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="md">
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${primary[700]}` }}>
          <Text fz={28} fw={700} c={primary[700]}>{total}</Text>
          <Text size="xs" c="dimmed">Total Observations</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.error}` }}>
          <Text fz={28} fw={700} c={semantic.error}>
            {filtered.filter(o => o.tier === 'critical_error').length}
          </Text>
          <Text size="xs" c="dimmed">Critical Errors</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.success}` }}>
          <Text fz={28} fw={700} c={semantic.success}>{remedyPct}%</Text>
          <Text size="xs" c="dimmed">Remedy Coverage</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder ta="center" style={{ borderTop: `3px solid ${semantic.info}` }}>
          <Text fz={28} fw={700} c={semantic.info}>{contributors.length}</Text>
          <Text size="xs" c="dimmed">Contributors</Text>
        </Paper>
      </SimpleGrid>

      {/* Remedy Coverage */}
      <div style={{ marginBottom: 'var(--mantine-spacing-md)' }}>
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Remedy Coverage</Text>
          <Group justify="space-between" mb={4}>
            <Text size="sm">{withRemedy} of {total} have remedies</Text>
            <Text size="sm" fw={600} style={{ color: remedyPct > 70 ? 'var(--color-success)' : remedyPct > 40 ? 'var(--color-warning)' : semantic.error }}>
              {remedyPct}%
            </Text>
          </Group>
          <Progress
            value={remedyPct}
            color={remedyPct > 70 ? 'var(--color-success)' : remedyPct > 40 ? 'var(--color-warning)' : semantic.error}
            size="lg"
            radius="xl"
            mb="sm"
          />
          {unremediedCritical.length > 0 && (
            <Paper p="xs" radius="sm" style={{ background: 'var(--background-error-light)', border: `1px solid ${semantic.error}` }}>
              <Text size="xs" fw={500} style={{ color: semantic.error }}>
                {unremediedCritical.length} critical/problem observation{unremediedCritical.length !== 1 ? 's' : ''} missing remedies
              </Text>
            </Paper>
          )}
        </Paper>
      </div>

      {/* Distribution Charts */}
      <SimpleGrid cols={{ base: 1, md: 3 }} mb="md">
        {/* Tier Distribution */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Tier Distribution</Text>
          <Stack gap="xs">
            {tierData.map(t => (
              <div key={t.tier}>
                <Group justify="space-between" mb={2}>
                  <Badge size="xs" style={{ background: t.bg, color: t.color }}>{t.label}</Badge>
                  <Text size="xs" fw={500}>{t.count} ({t.pct}%)</Text>
                </Group>
                <Progress value={t.pct} color={t.color} size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>

        {/* Category Breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">By Category</Text>
          <Stack gap="xs">
            {catData.map(c => (
              <div key={c.category}>
                <Group justify="space-between" mb={2}>
                  <Text size="xs" tt="capitalize">{c.category}</Text>
                  <Text size="xs" fw={500}>{c.count} ({c.pct}%)</Text>
                </Group>
                <Progress value={c.pct} color={primary[700]} size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>

        {/* Phase Breakdown */}
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">By Phase</Text>
          <Stack gap="xs">
            {phaseData.map(p => (
              <div key={p.phase}>
                <Group justify="space-between" mb={2}>
                  <Text size="xs" tt="capitalize">{p.phase}</Text>
                  <Text size="xs" fw={500}>{p.count} ({p.pct}%)</Text>
                </Group>
                <Progress value={p.pct} color="#03AC9E" size="sm" radius="xl" />
              </div>
            ))}
          </Stack>
        </Paper>
      </SimpleGrid>

      {/* Per-Incident Table */}
      {incidentBreakdown.length > 0 && (
        <Paper p="md" radius="sm" withBorder>
          <Text fw={600} size="sm" mb="sm">Observations by Incident</Text>
          <div style={{ overflow: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
              <thead>
                <tr style={{ background: 'var(--bg-secondary)', borderBottom: '1px solid var(--frost)' }}>
                  <th style={{ padding: '8px 16px', textAlign: 'left' }}>Incident</th>
                  <th style={{ padding: '8px 16px', textAlign: 'left' }}>Status</th>
                  <th style={{ padding: '8px 16px', textAlign: 'right' }}>Total</th>
                  <th style={{ padding: '8px 16px', textAlign: 'right' }}>Critical</th>
                  <th style={{ padding: '8px 16px', textAlign: 'right' }}>Remedied</th>
                  <th style={{ padding: '8px 16px', textAlign: 'right' }}>Coverage</th>
                </tr>
              </thead>
              <tbody>
                {incidentBreakdown.map(row => {
                  const coverage = row.count > 0 ? Math.round((row.remedied / row.count) * 100) : 0;
                  return (
                    <tr key={row.incident.id} style={{ borderBottom: '1px solid var(--frost)' }}>
                      <td style={{ padding: '8px 16px' }}>{row.incident.name}</td>
                      <td style={{ padding: '8px 16px' }}>
                        <Badge size="xs" color={row.incident.status === 'active' ? 'var(--color-success)' : row.incident.status === 'closed' ? '#939598' : '#C68700'} tt="capitalize">
                          {row.incident.status}
                        </Badge>
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>{row.count}</td>
                      <td style={{ padding: '8px 16px', textAlign: 'right', color: row.critical > 0 ? 'var(--color-error)' : undefined }}>
                        {row.critical}
                      </td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>{row.remedied}</td>
                      <td style={{ padding: '8px 16px', textAlign: 'right' }}>
                        <Text size="xs" fw={500} style={{ color: coverage > 70 ? 'var(--color-success)' : coverage > 40 ? 'var(--color-warning)' : semantic.error }}>
                          {coverage}%
                        </Text>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Paper>
      )}
    </div>
  );
}
